import { useEffect, useState } from "react";
import { ApiFailure, request } from "./api";
import { occupySeat, pressProd, registerPasskey } from "./passkey";
import type { Binding, Dashboard, Notification } from "./types";

function Header({ binding }: { binding: Binding }) {
  const logout = async () => {
    await request(binding, binding.http.api.logout, {});
    location.assign(binding.http.paths.login);
  };
  return <header>
    <strong>{binding.prose.brand}</strong>
    <nav>
      <a href={binding.http.paths.dashboard}>{binding.prose.dashboard_title}</a>
      <a href={binding.http.paths.org}>{binding.prose.org_title}</a>
      <a href={binding.http.paths.status}>{binding.prose.status_title}</a>
      <button onClick={logout}>{binding.prose.logout}</button>
    </nav>
  </header>;
}

function Login({ binding }: { binding: Binding }) {
  useEffect(() => {
    request(binding, binding.http.api.session)
      .then(() => location.replace(binding.http.paths.dashboard))
      .catch(() => undefined);
  }, [binding]);
  const login = () => {
    const target = new URL(binding.http.api.google_start, location.origin);
    target.searchParams.set(binding.http.protocol.next_query_parameter, binding.http.paths.dashboard);
    location.assign(target);
  };
  return <section>
    <h1>{binding.prose.login_title}</h1>
    <button onClick={login}>{binding.prose.login_action}</button>
  </section>;
}

function Notice({ binding, notice, reload }: { binding: Binding; notice: Notification; reload: () => void }) {
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);
  const action = async () => {
    setWorking(true);
    setFailed(false);
    try {
      if (notice.kind === binding.http.notification_kinds.register_passkey) await registerPasskey(binding);
      if (notice.kind === binding.http.notification_kinds.occupy_seat && notice.seat) await occupySeat(binding, notice.seat);
      if (notice.kind === binding.http.notification_kinds.press_prod && notice.request) await pressProd(binding, notice.request.id);
      reload();
    } catch {
      setWorking(false);
      setFailed(true);
    }
  };
  const label = notice.kind === binding.http.notification_kinds.register_passkey
    ? binding.prose.register_passkey
    : notice.kind === binding.http.notification_kinds.occupy_seat
      ? binding.prose.occupy_seat
      : binding.prose.press_prod;
  return <article>
    <h2>{label}</h2>
    {notice.seat && <p>{notice.seat}</p>}
    {notice.request && <pre>{JSON.stringify(notice.request, null, 2)}</pre>}
    <button disabled={working} onClick={action}>{label}</button>
    {failed && <output>{binding.prose.failure}</output>}
  </article>;
}

function DashboardPage({ binding }: { binding: Binding }) {
  const [revision, setRevision] = useState(0);
  const [dashboard, setDashboard] = useState<Dashboard>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    request<Dashboard>(binding, binding.http.api.dashboard)
      .then(setDashboard)
      .catch((error: unknown) => {
        if (error instanceof ApiFailure && error.status === 401) {
          location.replace(binding.http.paths.login);
        } else {
          setFailed(true);
        }
      });
  }, [binding, revision]);
  return <section>
    <h1>{binding.prose.dashboard_title}</h1>
    {!dashboard && !failed && <p>{binding.prose.loading}</p>}
    {failed && <output>{binding.prose.failure}</output>}
    {dashboard && <p><span>{dashboard.email}</span><br /><span>{dashboard.did}</span></p>}
    {dashboard?.notifications.length === 0 && <article>{binding.prose.no_notifications}</article>}
    {dashboard?.notifications.map((notice, index) =>
      <Notice key={index} binding={binding} notice={notice} reload={() => setRevision(value => value + 1)} />)}
  </section>;
}

function JsonPage({ binding, title, endpoint }: { binding: Binding; title: string; endpoint: string }) {
  const [value, setValue] = useState<unknown>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    request(binding, endpoint).then(setValue).catch((error: unknown) => {
      if (error instanceof ApiFailure && error.status === 401) {
        location.replace(binding.http.paths.login);
      } else {
        setFailed(true);
      }
    });
  }, [binding, endpoint]);
  return <section>
    <h1>{title}</h1>
    {value === undefined && !failed && <p>{binding.prose.loading}</p>}
    {failed && <output>{binding.prose.failure}</output>}
    {value !== undefined && <pre>{JSON.stringify(value, null, 2)}</pre>}
  </section>;
}


function MerkleFooter() {
  const root = document.body.dataset.merkleRoot || document.querySelector("meta[name=dgc-merkle-root]")?.getAttribute("content") || "";
  if (!root) return null;
  return <footer className="merkle">
    DGC Merkle Root: <code>{root}</code>
  </footer>;
}

export function App({ binding }: { binding: Binding }) {
  useEffect(() => {
    document.title = binding.prose.brand;
  }, [binding.prose.brand]);
  const path = location.pathname;
  return <>
    <Header binding={binding} />
    {path === binding.http.paths.login && <Login binding={binding} />}
    {path === binding.http.paths.dashboard && <DashboardPage binding={binding} />}
    {path === binding.http.paths.org && <JsonPage binding={binding} title={binding.prose.org_title} endpoint={binding.http.api.org} />}
    {path === binding.http.paths.status && <JsonPage binding={binding} title={binding.prose.status_title} endpoint={binding.http.api.platform_status} />}
    <MerkleFooter />
  </>;
}
